import React from "react";
import { Link } from "@heroui/link";

import { LowStockItem } from "../../types/dashboard";

interface LowStockWidgetProps {
  items: LowStockItem[];
}

const LowStockWidget: React.FC<LowStockWidgetProps> = ({ items }) => {
  if (!items || items.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 dark:text-gray-400">
        All inventory items are well stocked.
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th
              className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Item
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Current Stock
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Minimum
            </th>
            <th
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Status
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {items.map((item) => {
            // Determine the status color based on the percentage
            const getStatusColor = (percentage: number) => {
              if (percentage <= 25) return "text-red-600 dark:text-red-400";
              if (percentage <= 50)
                return "text-yellow-600 dark:text-yellow-400";

              return "text-blue-600 dark:text-blue-400";
            };

            const statusColor = getStatusColor(item.percentage);

            return (
              <tr
                key={item.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-900"
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <Link
                    className="text-sm font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400"
                    href={`/inventory/${item.id}`}
                  >
                    {item.name}
                  </Link>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-200">
                  {item.current_stock} {item.unit}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-200">
                  {item.minimum_stock} {item.unit}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <div className="relative w-16 h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden ml-auto">
                    <div
                      className={`absolute left-0 top-0 h-full ${
                        item.percentage <= 25
                          ? "bg-red-500"
                          : item.percentage <= 50
                            ? "bg-yellow-500"
                            : "bg-blue-500"
                      }`}
                      style={{ width: `${Math.min(item.percentage, 100)}%` }}
                    />
                  </div>
                  <span className={`text-xs mt-1 block ${statusColor}`}>
                    {item.percentage}%
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default LowStockWidget;
